
import React, { useEffect, useRef, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    LineElement,
    PointElement,
    Filler,
} from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import '../Styles/ChartVisualization.css';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    LineElement,
    PointElement,
    Filler
);

const ChartVisualization = ({ visualizations }) => {
    const chartRef = useRef(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [screenSize, setScreenSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 1200,
        height: typeof window !== 'undefined' ? window.innerHeight : 800
    });
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const [deviceOrientation, setDeviceOrientation] = useState('portrait');
    
    // Ensure viewport meta tag exists for proper mobile rendering
    useEffect(() => {
        let viewportMeta = document.querySelector('meta[name="viewport"]');
        if (!viewportMeta) {
            viewportMeta = document.createElement('meta');
            viewportMeta.name = 'viewport';
            viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes';
            document.head.appendChild(viewportMeta);
        } else {
            // Ensure existing viewport meta has proper settings
            const content = viewportMeta.getAttribute('content');
            if (!content.includes('width=device-width')) {
                viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes';
            }
        }
    }, []);
    
    // Check theme on component mount and listen for changes
    useEffect(() => {
        const updateTheme = () => {
            const theme = document.documentElement.getAttribute('data-theme');
            setIsDarkMode(theme === 'dark');
        };
        
        // Initial theme check
        updateTheme();
        
        // Listen for theme changes
        const observer = new MutationObserver(updateTheme);
        observer.observe(document.documentElement, { 
            attributes: true, 
            attributeFilter: ['data-theme'] 
        });
        
        return () => observer.disconnect();
    }, []);
    
    // Detect touch device and device orientation
    useEffect(() => {
        const checkTouchDevice = () => {
            const isTouchCapable = 'ontouchstart' in window || 
                                 navigator.maxTouchPoints > 0 || 
                                 navigator.msMaxTouchPoints > 0 ||
                                 window.DocumentTouch && document instanceof window.DocumentTouch;
            setIsTouchDevice(isTouchCapable);
        };
        
        const checkOrientation = () => {
            // Use a timeout to ensure we get the correct dimensions after orientation change
            setTimeout(() => {
                const width = window.innerWidth;
                const height = window.innerHeight;
                
                if (window.screen && window.screen.orientation) {
                    const angle = window.screen.orientation.angle;
                    setDeviceOrientation((angle === 90 || angle === -90) ? 'landscape' : 'portrait');
                } else {
                    // Fallback: compare width vs height
                    setDeviceOrientation(width > height ? 'landscape' : 'portrait');
                }
                
                // Also update screen size here to ensure sync
                setScreenSize({ width, height });
            }, 100);
        };
        
        checkTouchDevice();
        checkOrientation();
        
        // Listen for both orientationchange and resize events
        window.addEventListener('orientationchange', checkOrientation);
        window.addEventListener('resize', checkOrientation);
        
        return () => {
            window.removeEventListener('orientationchange', checkOrientation);
            window.removeEventListener('resize', checkOrientation);
        };
    }, []);
    
    // Handle screen size changes for responsive charts with debouncing
    useEffect(() => {
        let resizeTimeout;
        
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                setScreenSize({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }, 150); // Debounce resize events
        };
        
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, []);
    
    // Get responsive chart settings based on screen size
    const getResponsiveSettings = () => {
        const { width, height } = screenSize;
        const isVerySmallScreen = width <= 360;
        const isSmallScreen = width <= 480;
        const isMediumScreen = width <= 768;
        const isTablet = width <= 992;
        const isLandscapeMobile = isTouchDevice && deviceOrientation === 'landscape' && width <= 768;
        const isMobileDevice = isTouchDevice || width <= 768;
        
        console.log('Screen dimensions:', { width, height, isTouchDevice, deviceOrientation, isLandscapeMobile }); // Debug log
        
        // Very small screens (≤ 360px)
        if (isVerySmallScreen) {
            return {
                isMobile: true,
                isVerySmall: true,
                isTouchDevice,
                fontSize: {
                    title: 11,
                    legend: 8,
                    tooltip: 9,
                    ticks: 7
                },
                padding: {
                    title: { top: 2, bottom: 6 },
                    legend: 4,
                    tooltip: 4
                },
                legend: {
                    position: 'bottom',
                    maxItems: 2,
                    display: false // Hide legend on very small screens
                },
                aspectRatio: isLandscapeMobile ? 1.5 : 0.9,
                maxTicksLimit: 3,
                showTitle: false
            };
        }
        // Small phones (≤ 480px)
        else if (isSmallScreen) {
            return {
                isMobile: true,
                isSmall: true,
                isTouchDevice,
                fontSize: {
                    title: isLandscapeMobile ? 12 : 13,
                    legend: 9,
                    tooltip: 10,
                    ticks: 8
                },
                padding: {
                    title: { top: 3, bottom: 8 },
                    legend: 6,
                    tooltip: 5
                },
                legend: {
                    position: isLandscapeMobile ? 'right' : 'bottom',
                    maxItems: 3,
                    display: true
                },
                aspectRatio: isLandscapeMobile ? 1.6 : 1.0,
                maxTicksLimit: isLandscapeMobile ? 4 : 3,
                showTitle: !isLandscapeMobile
            };
        }
        // Medium phones/phablets (≤ 768px)
        else if (isMediumScreen) {
            return {
                isMobile: true,
                isTouchDevice,
                fontSize: {
                    title: isLandscapeMobile ? 14 : 15,
                    legend: 10,
                    tooltip: 11,
                    ticks: 9
                },
                padding: {
                    title: { top: 5, bottom: 12 },
                    legend: 8,
                    tooltip: 6
                },
                legend: {
                    position: isLandscapeMobile ? 'right' : 'bottom',
                    maxItems: 4,
                    display: true
                },
                aspectRatio: isLandscapeMobile ? 2.0 : 1.2,
                maxTicksLimit: isLandscapeMobile ? 5 : 4,
                showTitle: true
            };
        }
        // Tablets (≤ 992px)
        else if (isTablet) {
            return {
                isMobile: isMobileDevice,
                isTablet: true,
                isTouchDevice,
                fontSize: {
                    title: 16,
                    legend: 11,
                    tooltip: 12,
                    ticks: 10
                },
                padding: {
                    title: { top: 6, bottom: 16 },
                    legend: 10,
                    tooltip: 8
                },
                legend: {
                    position: 'top',
                    maxItems: 5,
                    display: true
                },
                aspectRatio: 1.5,
                maxTicksLimit: 6,
                showTitle: true
            };
        }
        // Desktop and larger
        else {
            return {
                isMobile: false,
                isTouchDevice: false,
                fontSize: {
                    title: 18,
                    legend: 12,
                    tooltip: 13,
                    ticks: 11
                },
                padding: {
                    title: { top: 10, bottom: 30 },
                    legend: 15,
                    tooltip: 12
                },
                legend: {
                    position: 'top',
                    maxItems: 8,
                    display: true
                },
                aspectRatio: 2,
                maxTicksLimit: 8,
                showTitle: true
            };
        }
    };
    
    // Get theme-aware colors
    const getThemeColors = () => {
        if (isDarkMode) {
            return {
                textColor: '#e5e7eb',
                mutedTextColor: '#9ca3af',
                gridColor: 'rgba(255, 255, 255, 0.1)',
                tooltipBg: 'rgba(31, 41, 55, 0.95)',
                tooltipBorder: 'rgba(255, 255, 255, 0.2)',
                chartBg: 'linear-gradient(145deg, #1e293b 0%, #334155 100%)',
                chartBorder: 'rgba(255, 255, 255, 0.1)'
            };
        } else {
            return {
                textColor: '#2c3e50',
                mutedTextColor: '#64748b',
                gridColor: 'rgba(0, 0, 0, 0.05)',
                // tooltipBg: 'rgba(0, 0, 0, 0.8)',
                // tooltipBorder: 'rgba(255, 255, 255, 0.1)',
                tooltipBg: 'rgba(31, 41, 55, 0.95)',
                tooltipBorder: 'rgba(255, 255, 255, 0.3)',
                chartBg: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                chartBorder: 'rgba(255, 255, 255, 0.18)'
            };
        }
    };
    
    // Create gradient backgrounds for charts
    const createGradient = (ctx, colorStart, colorEnd) => {
        if (!ctx) {
            return colorStart;
        }
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, colorStart);
        gradient.addColorStop(1, colorEnd);
        return gradient;
    };
    
    // Enhanced color palette with modern gradients and better aesthetics
    const generateColors = (count, ctx = null) => {
        const themeColors = getThemeColors();
        
        const gradientColors = [
            { start: 'rgba(102, 126, 234, 0.8)', end: 'rgba(102, 126, 234, 0.1)' },
            { start: 'rgba(240, 147, 251, 0.8)', end: 'rgba(240, 147, 251, 0.1)' },
            { start: 'rgba(79, 172, 254, 0.8)', end: 'rgba(79, 172, 254, 0.1)' },
            { start: 'rgba(67, 233, 123, 0.8)', end: 'rgba(67, 233, 123, 0.1)' },
            { start: 'rgba(250, 112, 154, 0.8)', end: 'rgba(250, 112, 154, 0.1)' },
            { start: 'rgba(168, 237, 234, 0.8)', end: 'rgba(168, 237, 234, 0.1)' },
            { start: 'rgba(255, 154, 158, 0.8)', end: 'rgba(255, 154, 158, 0.1)' },
            { start: 'rgba(161, 140, 209, 0.8)', end: 'rgba(161, 140, 209, 0.1)' },
            { start: 'rgba(250, 208, 196, 0.8)', end: 'rgba(250, 208, 196, 0.1)' },
            { start: 'rgba(132, 250, 176, 0.8)', end: 'rgba(132, 250, 176, 0.1)' },
        ];
        
        // Adjust opacity for dark mode
        const adjustedGradientColors = isDarkMode ? 
            gradientColors.map(color => ({
                start: color.start.replace('0.8)', '0.9)'),
                end: color.end.replace('0.1)', '0.2)')
            })) : gradientColors;
        
        // Fallback solid colors for better compatibility
        const solidColors = isDarkMode ? [
            'rgba(102, 126, 234, 0.9)',
            'rgba(240, 147, 251, 0.9)',
            'rgba(79, 172, 254, 0.9)',
            'rgba(67, 233, 123, 0.9)',
            'rgba(250, 112, 154, 0.9)',
            'rgba(168, 237, 234, 0.9)',
            'rgba(255, 154, 158, 0.9)',
            'rgba(161, 140, 209, 0.9)',
            'rgba(250, 208, 196, 0.9)',
            'rgba(132, 250, 176, 0.9)',
        ] : [
            'rgba(102, 126, 234, 0.8)',
            'rgba(240, 147, 251, 0.8)',
            'rgba(79, 172, 254, 0.8)',
            'rgba(67, 233, 123, 0.8)',
            'rgba(250, 112, 154, 0.8)',
            'rgba(168, 237, 234, 0.8)',
            'rgba(255, 154, 158, 0.8)',
            'rgba(161, 140, 209, 0.8)',
            'rgba(250, 208, 196, 0.8)',
            'rgba(132, 250, 176, 0.8)',
        ];
        
        return Array.from({ length: count }, (_, i) => {
            if (ctx) {
                const colorPair = adjustedGradientColors[i % adjustedGradientColors.length];
                return createGradient(ctx, colorPair.start, colorPair.end);
            }
            return solidColors[i % solidColors.length];
        });
    };

    const generateBorderColors = (count) => {
        const borderColors = [
            'rgba(102, 126, 234, 1)',
            'rgba(240, 147, 251, 1)',
            'rgba(79, 172, 254, 1)',
            'rgba(67, 233, 123, 1)',
            'rgba(250, 112, 154, 1)',
            'rgba(168, 237, 234, 1)',
            'rgba(255, 154, 158, 1)',
            'rgba(161, 140, 209, 1)',
            'rgba(250, 208, 196, 1)',
            'rgba(132, 250, 176, 1)',
        ];
        
        return Array.from({ length: count }, (_, i) => borderColors[i % borderColors.length]);
    };

    const generateHoverColors = (count) => {
        const hoverColors = [
            'rgba(102, 126, 234, 0.9)',
            'rgba(240, 147, 251, 0.9)',
            'rgba(79, 172, 254, 0.9)',
            'rgba(67, 233, 123, 0.9)',
            'rgba(250, 112, 154, 0.9)',
            'rgba(168, 237, 234, 0.9)',
            'rgba(255, 154, 158, 0.9)',
            'rgba(161, 140, 209, 0.9)',
            'rgba(250, 208, 196, 0.9)',
            'rgba(132, 250, 176, 0.9)',
        ];
        
        return Array.from({ length: count }, (_, i) => hoverColors[i % hoverColors.length]);
    };

    const renderChart = (visualization, index) => {
        const { type, labels, values, title } = visualization;
        const themeColors = getThemeColors();
        
        if (!values || !Array.isArray(values) || values.length === 0) {
            return (
                <div key={index} className="chart-error">
                    <p>Invalid chart data format</p>
                </div>
            );
        }

        // Handle null or missing labels by generating meaningful ones
        let processedLabels = labels;
        if (!labels || labels.length !== values.length || labels.every(label => label === null || label === undefined)) {
            processedLabels = values.map((_, i) => `Item ${i + 1}`);
        } else {
            // Process labels to ensure they are strings
            processedLabels = labels.map((label, i) => {
                if (typeof label === 'string') {
                    return label;
                } else if (typeof label === 'object' && label !== null) {
                    // If it's an object, try to extract a string value
                    const stringKeys = ['name', 'label', 'title', 'key', 'category'];
                    for (const key of stringKeys) {
                        if (typeof label[key] === 'string') {
                            return label[key];
                        }
                    }
                    // If no string key found, try to get the first string property
                    const firstStringValue = Object.values(label).find(val => typeof val === 'string');
                    if (firstStringValue !== undefined) {
                        return firstStringValue;
                    }
                    return `Item ${i + 1}`; // Default label
                } else if (label !== null && label !== undefined) {
                    return String(label);
                }
                return `Item ${i + 1}`;
            });
        }

        // Process values to ensure they are numbers
        let processedValues = values.map((value, index) => {
            if (typeof value === 'number') {
                return value;
            } else if (typeof value === 'object' && value !== null) {
                // If it's an object, try to extract a numeric value
                // Common patterns: {value: 123}, {count: 123}, {amount: 123}, etc.
                const numericKeys = ['value', 'count', 'amount', 'total', 'sum', 'revenue', 'sales', 'data', 'y'];
                for (const key of numericKeys) {
                    if (typeof value[key] === 'number') {
                        return value[key];
                    }
                }
                // If no numeric key found, try to get the first numeric property
                const firstNumericValue = Object.values(value).find(val => typeof val === 'number');
                if (firstNumericValue !== undefined) {
                    return firstNumericValue;
                }
                return 0; // Default to 0 if no numeric value found
            } else if (typeof value === 'string') {
                // Try to parse string as number
                const parsed = parseFloat(value);
                return isNaN(parsed) ? 0 : parsed;
            }
            return 0; // Default for any other type
        });

        const colors = generateColors(processedValues.length);
        const borderColors = generateBorderColors(processedValues.length);
        const hoverColors = generateHoverColors(processedValues.length);

        const chartData = {
            labels: processedLabels,
            datasets: [
                {
                    label: title || 'Data',
                    data: processedValues,
                    backgroundColor: colors,
                    borderColor: borderColors,
                    hoverBackgroundColor: hoverColors,
                    borderWidth: 3,
                    borderRadius: type === 'bar' ? 8 : 0,
                    borderSkipped: false,
                    tension: type === 'line' ? 0.4 : 0, // Smooth curves for line charts
                    pointBackgroundColor: borderColors,
                    pointBorderColor: isDarkMode ? '#1e293b' : '#ffffff',
                    pointBorderWidth: 3,
                    pointRadius: type === 'line' ? 6 : 0,
                    pointHoverRadius: type === 'line' ? 8 : 0,
                    fill: type === 'line' ? 'start' : false, // Fill area under line chart
                    shadowOffsetX: 3,
                    shadowOffsetY: 3,
                    shadowColor: 'rgba(0, 0, 0, 0.2)',
                    shadowBlur: 10,
                }
            ]
        };

        const responsiveSettings = getResponsiveSettings();
        
        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            aspectRatio: responsiveSettings.aspectRatio,
            devicePixelRatio: responsiveSettings.isTouchDevice ? (window.devicePixelRatio || 1) : 2,
            layout: {
                padding: {
                    left: responsiveSettings.isMobile ? 5 : 10,
                    right: responsiveSettings.isMobile ? 5 : 10,
                    top: responsiveSettings.isMobile ? 5 : 10,
                    bottom: responsiveSettings.isMobile ? 5 : 10
                }
            },
            interaction: {
                intersect: false,
                mode: responsiveSettings.isMobile ? 'nearest' : 'index',
            },
            animation: {
                duration: responsiveSettings.isVerySmall ? 200 : (responsiveSettings.isMobile ? 400 : 1200),
                easing: 'easeInOutQuart',
                delay: (context) => {
                    if (responsiveSettings.isVerySmall) return 0;
                    return responsiveSettings.isMobile ? context.dataIndex * 20 : context.dataIndex * 100;
                },
            },
            elements: {
                bar: {
                    borderRadius: responsiveSettings.isVerySmall ? 1 : (responsiveSettings.isMobile ? 3 : 8),
                },
                point: {
                    hoverRadius: responsiveSettings.isVerySmall ? 3 : (responsiveSettings.isMobile ? 5 : 8),
                    radius: responsiveSettings.isVerySmall ? 1 : (responsiveSettings.isMobile ? 3 : 6),
                }
            },
            plugins: {
                legend: {
                    display: responsiveSettings.legend.display !== false,
                    position: responsiveSettings.legend.position,
                    align: 'center',
                    maxHeight: responsiveSettings.isMobile ? (responsiveSettings.isVerySmall ? 40 : 60) : 100,
                    labels: {
                        boxWidth: responsiveSettings.isMobile ? (responsiveSettings.isVerySmall ? 8 : 10) : 12,
                        boxHeight: responsiveSettings.isMobile ? (responsiveSettings.isVerySmall ? 8 : 10) : 12,
                        borderRadius: 6,
                        useBorderRadius: true,
                        font: {
                            size: responsiveSettings.fontSize.legend,
                            weight: '500',
                            family: "'Segoe UI', 'Roboto', sans-serif"
                        },
                        color: themeColors.textColor,
                        padding: responsiveSettings.padding.legend,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        textAlign: responsiveSettings.isMobile ? 'center' : 'left',
                        filter: function(legendItem, chartData) {
                            // Limit legend items on mobile
                            return legendItem.datasetIndex < responsiveSettings.legend.maxItems;
                        }
                    }
                },
                title: {
                    display: !!title && responsiveSettings.showTitle,
                    text: title,
                    font: {
                        size: responsiveSettings.fontSize.title,
                        weight: '600',
                        family: "'Segoe UI', 'Roboto', sans-serif"
                    },
                    color: themeColors.textColor,
                    padding: responsiveSettings.padding.title
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: themeColors.tooltipBg,
                    // titleColor: themeColors.textColor,
                    // bodyColor: themeColors.textColor,
                    titleColor: isDarkMode ? '#e5e7eb' : '#ffffff',
                    bodyColor: isDarkMode ? '#e5e7eb' : '#ffffff',
                    borderColor: themeColors.tooltipBorder,
                    borderWidth: 1,
                    cornerRadius: responsiveSettings.isMobile ? (responsiveSettings.isVerySmall ? 6 : 8) : 12,
                    displayColors: !responsiveSettings.isVerySmall,
                    titleFont: {
                        size: responsiveSettings.fontSize.tooltip + (responsiveSettings.isVerySmall ? 0 : 1),
                        weight: '600'
                    },
                    bodyFont: {
                        size: responsiveSettings.fontSize.tooltip,
                        weight: '500'
                    },
                    padding: responsiveSettings.padding.tooltip,
                    caretPadding: responsiveSettings.isMobile ? 4 : 10,
                    multiKeyBackground: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    position: responsiveSettings.isMobile ? 'nearest' : 'average',
                    callbacks: {
                        label: function(context) {
                            const label = context.label || '';
                            let value = context.parsed?.y || context.parsed || context.raw;
                            
                            // Handle case where value might still be an object
                            if (typeof value === 'object' && value !== null) {
                                // Try to extract numeric value from object
                                const numericKeys = ['value', 'count', 'amount', 'total', 'sum', 'revenue', 'sales', 'data', 'y'];
                                for (const key of numericKeys) {
                                    if (typeof value[key] === 'number') {
                                        value = value[key];
                                        break;
                                    }
                                }
                                // If still an object, get first numeric property
                                if (typeof value === 'object') {
                                    const firstNumericValue = Object.values(value).find(val => typeof val === 'number');
                                    value = firstNumericValue !== undefined ? firstNumericValue : 0;
                                }
                            }
                            
                            // Ensure value is a number
                            if (typeof value !== 'number') {
                                value = parseFloat(value) || 0;
                            }
                            
                            // Format large numbers
                            let formattedValue = value;
                            if (typeof value === 'number') {
                                if (value >= 1e12) {
                                    formattedValue = (value / 1e12).toFixed(2) + 'T';
                                } else if (value >= 1e9) {
                                    formattedValue = (value / 1e9).toFixed(2) + 'B';
                                } else if (value >= 1e6) {
                                    formattedValue = (value / 1e6).toFixed(2) + 'M';
                                } else if (value >= 1e3) {
                                    formattedValue = (value / 1e3).toFixed(2) + 'K';
                                } else {
                                    formattedValue = value.toLocaleString();
                                }
                            }
                            
                            return `${label}: ${formattedValue}`;
                        }
                    }
                }
            },
            scales: type === 'bar' || type === 'line' ? {
                y: {
                    beginAtZero: true,
                    grid: {
                        color: themeColors.gridColor,
                        lineWidth: responsiveSettings.isMobile ? 0.5 : 1,
                        drawBorder: false,
                    },
                    border: {
                        display: false
                    },
                    ticks: {
                        font: {
                            size: responsiveSettings.fontSize.ticks,
                            weight: '500',
                            family: "'Segoe UI', 'Roboto', sans-serif"
                        },
                        color: themeColors.mutedTextColor,
                        padding: responsiveSettings.isMobile ? 3 : 10,
                        maxTicksLimit: responsiveSettings.maxTicksLimit,
                        callback: function(value) {
                            // More aggressive formatting on mobile
                            if (responsiveSettings.isMobile) {
                                if (value >= 1e9) {
                                    return (value / 1e9).toFixed(0) + 'B';
                                } else if (value >= 1e6) {
                                    return (value / 1e6).toFixed(0) + 'M';
                                } else if (value >= 1e3) {
                                    return (value / 1e3).toFixed(0) + 'K';
                                } else if (responsiveSettings.isVerySmall && value >= 100) {
                                    return Math.round(value);
                                }
                            } else {
                                if (value >= 1e12) {
                                    return (value / 1e12).toFixed(1) + 'T';
                                } else if (value >= 1e9) {
                                    return (value / 1e9).toFixed(1) + 'B';
                                } else if (value >= 1e6) {
                                    return (value / 1e6).toFixed(1) + 'M';
                                } else if (value >= 1e3) {
                                    return (value / 1e3).toFixed(1) + 'K';
                                }
                            }
                            return value;
                        }
                    }
                },
                x: {
                    grid: {
                        display: false,
                    },
                    border: {
                        display: false
                    },
                    ticks: {
                        font: {
                            size: responsiveSettings.fontSize.ticks,
                            weight: '500',
                            family: "'Segoe UI', 'Roboto', sans-serif"
                        },
                        color: themeColors.mutedTextColor,
                        padding: responsiveSettings.isMobile ? 3 : 10,
                        maxRotation: responsiveSettings.isMobile ? 
                            (responsiveSettings.isVerySmall ? 0 : 30) : 45,
                        minRotation: 0,
                        maxTicksLimit: responsiveSettings.maxTicksLimit,
                        callback: function(value, index) {
                            // Truncate long labels on mobile
                            if (responsiveSettings.isMobile) {
                                const label = this.getLabelForValue(value);
                                if (typeof label === 'string') {
                                    const maxLength = responsiveSettings.isVerySmall ? 6 : 10;
                                    return label.length > maxLength ? 
                                        label.substring(0, maxLength) + '...' : label;
                                }
                            }
                            return this.getLabelForValue(value);
                        }
                    }
                }
            } : {}
        };

        // Chart container class for CSS styling
        const chartContainerClass = 'chart-container-enhanced';

        switch (type.toLowerCase()) {
            case 'bar':
                return (
                    <div key={index} className={chartContainerClass}>
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                );
            case 'pie':
                return (
                    <div key={index} className={chartContainerClass}>
                        <Pie data={chartData} options={chartOptions} />
                    </div>
                );
            case 'doughnut':
                return (
                    <div key={index} className={chartContainerClass}>
                        <Doughnut data={chartData} options={chartOptions} />
                    </div>
                );
            case 'line':
                return (
                    <div key={index} className={chartContainerClass}>
                        <Line data={chartData} options={chartOptions} />
                    </div>
                );
            default:
                // Default to bar chart for unknown types
                return (
                    <div key={index} className={chartContainerClass}>
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                );
        }
    };

    if (!visualizations || !Array.isArray(visualizations) || visualizations.length === 0) {
        return null;
    }

    return (
        <div className="chart-visualization-container">
            {visualizations.map((visualization, index) => (
                <div key={index} className="chart-wrapper">
                    {renderChart(visualization, index)}
                </div>
            ))}
        </div>
    );
};

export default ChartVisualization;
